import { RequestService, type RequestOptions } from '../request-service';
import type RpcModule from '@wix/ambassador-devcenter-ams-v1-app-market-search/rpc';
import type HttpModule from '@wix/ambassador-devcenter-ams-v1-app-market-search/http';
import type {
  SearchRequest,
  SearchResponse,
} from '@wix/ambassador-devcenter-ams-v1-app-market-search/types';
import type { AmbassadorBootstrap } from '@wix/ambassador-bootstrap-plugin';
import type { IHttpClient } from '@wix/http-client';
import type { QueryClient } from '@tanstack/react-query';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import { SearchApps } from './search-apps';
import type { SortType } from '../tag-apps';
import type { QueryAppsFilter } from '../../types';
import type { AppsResult, Paging, SearchAppsPayload } from './types';
import { AppBenefitsService } from '../app-benefits';
import { AppsRecommenderService } from '../apps-recommender';
import { createSearchRequest } from './search-filter-utils';

type RpcModuleType = typeof RpcModule;
type HttpModuleType = typeof HttpModule;

export class SearchAppsService extends RequestService<
  RpcModuleType,
  HttpModuleType
> {
  private readonly appBenefitsService: AppBenefitsService;
  private readonly tagIds = {
    benefits: 'eab81c12-5916-4ec7-bb5b-a9bdcdfea4af',
    recommendedForYou: 'b561bac9-1dda-491f-ba14-8e0f5fd89f2c',
    saleApps: '5b2190c5-bb1b-481f-a879-fd95156f3ff0',
  };
  private readonly appsRecommenderService: AppsRecommenderService;

  constructor(
    rpcClient?: AmbassadorBootstrap,
    httpClient?: IHttpClient,
    queryClient?: QueryClient,
    errorHandler?: ErrorHandlerPublicAPI,
  ) {
    super(rpcClient, httpClient, queryClient, errorHandler);
    this.appBenefitsService = new AppBenefitsService(
      rpcClient,
      httpClient,
      queryClient,
    );
    this.appsRecommenderService = new AppsRecommenderService(
      rpcClient,
      httpClient,
      queryClient,
    );
  }

  protected async rpcModule(): Promise<RpcModuleType> {
    return import('@wix/ambassador-devcenter-ams-v1-app-market-search/rpc');
  }

  protected async httpModule(): Promise<HttpModuleType> {
    return import('@wix/ambassador-devcenter-ams-v1-app-market-search/http');
  }

  public async apps(
    payload: SearchAppsPayload,
    options?: RequestOptions,
  ): Promise<AppsResult> {
    const {
      paging,
      filter = {},
      sortTypes,
      languageCode,
      customParams,
      skipRollout,
    } = payload;

    if (filter?.tagId === this.tagIds.recommendedForYou) {
      return this.searchRecommendedForYouApps({ paging, languageCode });
    }

    return this.searchApps(
      {
        paging,
        filter,
        sortTypes,
        skipRollout,
      },
      options,
      customParams,
    );
  }

  private async searchRecommendedForYouApps({
    paging,
    languageCode,
  }: {
    paging?: Paging;
    languageCode?: string;
  }): Promise<AppsResult> {
    const recommendedAppIds =
      await this.appsRecommenderService.getAppsRecommender(
        paging?.limit ?? 4,
        languageCode,
      );

    return {
      appIds: recommendedAppIds,
      hasMoreApps: false,
      totalApps: recommendedAppIds.length,
    };
  }

  private async searchApps(
    {
      paging,
      filter,
      sortTypes,
      skipRollout,
    }: {
      paging?: Paging;
      filter?: QueryAppsFilter;
      sortTypes?: SortType[];
      skipRollout?: boolean;
    },
    options?: RequestOptions,
    customParams?: Record<string, any>,
  ): Promise<AppsResult> {
    const req = await createSearchRequest({
      filter,
      paging,
      sortTypes,
      appBenefitsService: this.appBenefitsService,
      customParams,
      skipRollout,
    });

    // handle case where empty response is returned
    if (!req) {
      return {
        appIds: [],
        hasMoreApps: false,
        totalApps: 0,
      };
    }

    const response = await this.request<SearchRequest, SearchResponse>({
      methodName: 'search',
      payload: req,
      options,
    });

    return new SearchApps(
      response.webSolutions || [],
      response.paging,
    ).toJSON();
  }
}
